import { downgradeComponent, downgradeInjectable } from '@angular/upgrade/static';
import { Router } from '@angular/router';

import httpInterceptorConfig from './http-interceptor.ajs';
import translateProviderConfig from './translate.ajs'; // remove when changed in .ts (instant, filter, translate)
import provideConfig from './provide.ajs';
import rollbarProviderConfig from './rollbar.ajs';
import appInitialize from './initialize.ajs';

// Constants
import CONFIGURATIONS from '../_constants/constants';
import TIME_ZONES from '../_constants/time-zones';

// Services
import { AbilityServiceAjs } from './_shared/services/ability.service';
import { AvailabilityService } from './_shared/services/availability.service';
import { CropImageService } from './_shared/services/crop-image/crop-image.service';
import { DocumentService } from './_shared/services/document.service';
import { FlashService } from './_shared/services/flash.service';
import { GoogleMapService } from './_shared/services/google-map.service';
import { LocalStoreService } from './_shared/services/local-storage.service';
import { ModalWindowConfirmService } from './_shared/services/window-modal-confirm/window-modal-confirm.service';
import { PermissionService } from './_shared/services/permission.service';
import { ProductService } from './_shared/services/product.service';
import { RestResourceService } from './_shared/services/rest-resource.service';
import { SessionServiceAjs } from './_shared/services/session.service';
import { TagService } from './_shared/services/tag.service';
import { TransactionReportService } from './_shared/services/transaction-report.service';
import { TranslationModalService } from './_shared/services/translation-modal.service';
import { UploadService } from './_shared/services/upload.service';
import { UtilServiceAjs } from './_shared/services/util.service';
import { WindowModalService } from './_shared/services/window-modal.service';

// Directives
import { AccountNameAvailableValidator } from './_shared/directives/account-name-available-validator.directive';
import { AccountSubdomainAvailableValidatorDirective } from './_shared/directives/account-subdomain-available-validator.directive';
import { AllowPatternDirective } from './_shared/directives/allow-pattern.directive';
import { CompanyWebsiteAvailableValidatorDirective } from './_shared/directives/company-website-available-validator.directive';
import { CompanyTaxIdNumberAvailableValidatorDirective } from './_shared/directives/company-tax-id-number-available-validator.directive';
import { CompareToUniqueDirective } from './_shared/directives/compare-to-unique.directive';
import { CompareToValidatorDirective } from './_shared/directives/compare-to-validator.directive';
import { ConvertToUppercaseDirective } from './_shared/directives/convert-to-uppercase.directive';
import { CurrencyFormatDirective } from './_shared/directives/currency-format.directive';
import { CurrencyInputDirective } from './_shared/directives/currency-input.directive';
import { FancyboxDirective } from './_shared/directives/fancybox.directive';
import { FileDirective } from './_shared/directives/file.directive';
import { FileModelDirective } from './_shared/directives/file-model.directive';
import { HttpPrefixDirective } from './_shared/directives/http-prefix.directive';
import { InputNumberMaxLengthDirective } from './_shared/directives/input-number-max-length.directive';
import { InputValidatorDirective } from './_shared/directives/input-validator.directive';
import { LoadingDataSpinnerDirective } from './_shared/directives/loading-data-spinner.directive';
import { MapDirective } from './_shared/directives/google-map.directive';
import { MinValueValidatorDirective } from './_shared/directives/min-value-validator.directive';
import { ModalDirective } from './_shared/directives/modal/modal.directive';
import { NgLoadingDirective } from './_shared/directives/ng-loading.directive';
import { NgThumbDirective } from './_shared/directives/ng-thumb.directive';
import { NumberConverterDirective } from './_shared/directives/number-converter.directive';
import { OnBlurChangeDirective } from './_shared/directives/on-blur-change.directive';
import { OnEnterBlurDirective } from './_shared/directives/on-enter-blur.directive';
import { OnEnterPressDirective } from './_shared/directives/on-enter-press.directive';
import { PasswordCharactersValidatorDirective } from './_shared/directives/password-characters-validator.directive';
import { PreventSpaceDirective } from './_shared/directives/prevent-space.directive';
import { SelectOnClickDirective } from './_shared/directives/select-on-click.directive';
import { UiSelectRequiredDirective } from './_shared/directives/ui-select-required.directive';
import { UserEmailAvailableValidatorDirective } from './_shared/directives/user-email-available-validator.directive';
import { UserUsernameAvailableValidatorDirective } from './_shared/directives/user-username-available-validator.directive';

// Pipes
import { CapitalizePipe } from './_shared/pipes/capitalize.pipe';
import { DigitsPipe } from './_shared/pipes/digits.pipe';
import { MomentPipe } from './_shared/pipes/moment.pipe';
import { NumberFormatPipe } from './_shared/pipes/number-format.pipe';
import { PropsFilterPipe } from './_shared/pipes/props-filter.pipe';
import { TimePipe } from './_shared/pipes/time.pipe';
import { TwoDigitsPipe } from './_shared/pipes/two-digits.pipe';

// Pages
import { RegisterPage } from '../_pages/register/register-page'; // Pending upgrade

// Shared components
import { AvailabilityCalendarComponent } from './_shared/components/availability-calendar/availability-calendar.component';
import { DocumentAtvCrReceptionStatusLabelComponent } from './_shared/components/document-atv-cr-reception-status-label.component';
import { DocumentAtvCrStatusLabelComponent } from './_shared/components/document-atv-cr-status-label.component';
import { DocumentProductTaxesExonerationsComponent } from './_shared/components/document-product-taxes-exonerations/document-product-taxes-exonerations.component';
import { GoogleDirectionsMapComponent } from './_shared/components/google-directions-map.component';
import { HotelAvailabilityCalendarComponent } from './_shared/components/hotel-availability-calendar/hotel-availability-calendar.component';
import { ItemDetailStandardActionsComponent } from './_shared/components/item-detail-standard-actions/item-detail-standard-actions.component';
import { ListActionsComponent } from './_shared/components/list-actions/list-actions.component';
import { NotifyDocumentToClientComponent } from './_shared/components/notify-document-to-client/notify-document-to-client.component';
import { ProductTaxExonerationComponent } from './_shared/components/product-tax-exoneration/product-tax-exoneration.component';
import { SearchCabysComponent } from './_shared/components/search-cabys/search-cabys.component';
import { SearchClientInputComponent } from './_shared/components/search-client-input/search-client-input.component';
import { SearchDiscountCouponInputComponent } from './_shared/components/search-discount-coupon-input/search-discount-coupon-input.component';
import { SearchFormActionsComponent } from './_shared/components/search-form-actions/search-form-actions.component';
import { SearchFormExportsComponent } from './_shared/components/search-form-exports/search-form-exports.component';
import { SearchProductInputComponent } from './_shared/components/search-product-input/search-product-input.component';
import { SearchProviderInputComponent } from './_shared/components/search-provider-input/search-provider-input.component';
import { SearchTextInputComponent } from './_shared/components/search-text-input/search-text-input.component';
import { SortByComponent } from './_shared/components/sort-by/sort-by.component';
import { TableFooterComponent } from './_shared/components/table-footer/table-footer.component';

import { RelatedTransactionDetailComponent } from './_shared/components/related-transactions/related-transaction-detail.component';
import { RelatedTransactionService } from './_shared/components/related-transactions/shared/related-transaction.service';

import { PhotoListComponent } from './_shared/components/photo-list/photo-list.component';
import { PhotoService } from './_shared/components/photo-list/shared/photo.service';
import { PhotoModalService } from './_shared/components/photo-list/shared/photo.modal.service';

// New Angular files - if we need to use it in angularjs - downgrading
// import { LanguageSelectorComponent } from '../_shared/components/language-selector.component';

import { AbilityService } from '../_shared/services/ability.service';
import { AlertService } from '../_shared/services/alert.service';
import { BreadCrumbsService } from '../_shared/services/breadcrumbs.service';
import { CategoryService } from '../categories/shared/category.service';
import { CategoryModalService } from '../categories/shared/category-modal.service';
import { CompanyService } from '../companies/shared/company.service';
import { CompanyModalService } from '../companies/shared/company-modal.service';
import { CountryService } from '../countries/shared/country.service';
import { CountryModalService } from '../countries/shared/country-modal.service';
import { CurrencyService } from '../currencies/shared/currency.service';
import { CurrencyModalService } from '../currencies/shared/currency-modal.service';
import { FacilityService } from '../facilities/shared/facility.service';
import { FacilityModalService } from '../facilities/shared/facility-modal.service';
import { LocationService } from '../locations/shared/location.service';
import { LocationModalService } from '../locations/shared/location-modal.service';
import { OccupancyTypeService } from '../occupancy-types/shared/occupancy-type.service';
import { OccupancyTypeModalService } from '../occupancy-types/shared/occupancy-type-modal.service';
import { PickupPlaceService } from '../pickup-places/shared/pickup-place.service';
import { PickupPlaceModalService } from '../pickup-places/shared/pickup-place-modal.service';
import { RoomTypeService } from '../room-types/shared/room-type.service';
import { RoomTypeModalService } from '../room-types/shared/room-type-modal.service';
import { PaxRangeService } from '../pax-ranges/shared/pax-range.service';
import { SessionService } from '../_shared/services/session.service';
import { SeasonService } from '../seasons/shared/season.service';
import { SeasonModalService } from '../seasons/shared/season-modal.service';
import { UserService } from '../users/shared/user.service';
import { UserModalService } from '../users/shared/user-modal.service';
import { WhatToBringService } from '../what-to-bring/shared/what-to-bring.service';
import { WhatToBringModalService } from '../what-to-bring/shared/what-to-bring-modal.service';
import { ZoneService } from '../zones/shared/zone.service';
import { ZoneModalService } from '../zones/shared/zone-modal.service';
import { ChartAccountService } from '../chart-accounts/shared/chart-account.service';

import { ProductCategoryAccountingComponent } from '../product-categories/components/product-category-accounting/product-category-accounting.component';
import { ProductAccountingComponent } from '../_shared/components/product-accounting/product-accounting.component';
import { FooterCompanyComponent } from '../_layout/footer-company/footer-company.component';
import { FooterWebsiteComponent } from '../_layout/footer-website/footer-website.component';

import { UtilService } from '../_shared/services/util.service';
import { TranslateService } from '@ngx-translate/core';

angular
  .module('app', [
    'ngRoute',
    'ngAnimate',
    'ngCookies',
    'ngMessages',
    'ngResource',
    'ngSanitize',
    'ngTouch',
    'angularFileUpload',
    'angularjs-dropdown-multiselect',
    'angularModalService',
    'angularMoment',
    'daterangepicker',
    'frapontillo.bootstrap-switch',
    'moment-picker',
    'ngFileSaver',
    'ngFileUpload',
    'pascalprecht.translate',
    'tandibar/ng-rollbar',
    'textAngular',
    'timepickerPop',
    'ui.bootstrap',
    'ui.mask',
    'ui.select',
    'ui.sortable',
  ])
  .config(httpInterceptorConfig)
  .config(translateProviderConfig)
  .config(provideConfig)
  .config(rollbarProviderConfig)
  .constant('CONFIGURATIONS', CONFIGURATIONS)
  .constant('TIME_ZONES', TIME_ZONES)
  .run(appInitialize)

  // Services
  .service('AbilityService', AbilityServiceAjs)
  .service('AvailabilityService', AvailabilityService)
  .service('CropImageService', CropImageService)
  .service('DocumentService', DocumentService)
  .service('FlashService', FlashService)
  .service('GoogleMapService', GoogleMapService)
  .service('LocalStoreService', LocalStoreService)
  .service('ModalWindowConfirmService', ModalWindowConfirmService)
  .service('PermissionService', PermissionService)
  .service('ProductService', ProductService)
  .service('PhotoService', PhotoService)
  .service('PhotoModalService', PhotoModalService)
  .service('RelatedTransactionService', RelatedTransactionService)
  .service('RestResource', RestResourceService)
  .service('SessionService', SessionServiceAjs)
  .service('TransactionReportService', TransactionReportService)
  .service('TranslationModalService', TranslationModalService)
  .service('UploadService', UploadService)
  .service('TagService', TagService)
  .service('UtilService', UtilServiceAjs)
  .service('WindowModalService', WindowModalService)

  // Directives
  .directive('accountNameAvailable', AccountNameAvailableValidator)
  .directive('accountSubdomainAvailableValidator', AccountSubdomainAvailableValidatorDirective)
  .directive('allowPattern', AllowPatternDirective)
  .directive('companyWebsiteAvailableValidator', CompanyWebsiteAvailableValidatorDirective)
  .directive('companyTaxIdNumberAvailableValidator', CompanyTaxIdNumberAvailableValidatorDirective)
  .directive('compareToUnique', CompareToUniqueDirective)
  .directive('compareToValidator', CompareToValidatorDirective)
  .directive('convertToUppercase', ConvertToUppercaseDirective)
  .directive('currencyFormat', CurrencyFormatDirective)
  .directive('currencyInput', CurrencyInputDirective)
  .directive('fancybox', FancyboxDirective)
  .directive('file', FileDirective)
  .directive('fileModel', FileModelDirective)
  .directive('httpPrefix', HttpPrefixDirective)
  .directive('inputNumberMaxLength', InputNumberMaxLengthDirective)
  .directive('inputValidator', InputValidatorDirective)
  .directive('loadingDataSpinner', LoadingDataSpinnerDirective)
  .directive('map', MapDirective)
  .directive('modal', ModalDirective)
  .directive('minValueValidator', MinValueValidatorDirective)
  .directive('ngLoading', NgLoadingDirective)
  .directive('ngThumb', NgThumbDirective)
  .directive('numberConverter', NumberConverterDirective)
  .directive('onBlurChange', OnBlurChangeDirective)
  .directive('onEnterBlur', OnEnterBlurDirective)
  .directive('onEnterPress', OnEnterPressDirective)
  .directive('passwordCharactersValidator', PasswordCharactersValidatorDirective)
  .directive('preventSpace', PreventSpaceDirective)
  .directive('selectOnClick', SelectOnClickDirective)
  .directive('uiSelectRequired', UiSelectRequiredDirective)
  .directive('userEmailAvailableValidator', UserEmailAvailableValidatorDirective)
  .directive('userUsernameAvailableValidator', UserUsernameAvailableValidatorDirective)

  // Pipes
  .filter('capitalize', CapitalizePipe)
  .filter('digits', DigitsPipe)
  .filter('moment', MomentPipe)
  .filter('numberFormat', NumberFormatPipe)
  .filter('propsFilter', PropsFilterPipe)
  .filter('time', TimePipe)
  .filter('twoDigits', TwoDigitsPipe)

  // Pages
  .component('registerPage', RegisterPage)

  // Shared components
  .component('availabilityCalendar', AvailabilityCalendarComponent)
  .component('documentProductTaxesExonerations', DocumentProductTaxesExonerationsComponent)
  .component('documentAtvCrReceptionStatusLabel', DocumentAtvCrReceptionStatusLabelComponent)
  .component('documentAtvCrStatusLabel', DocumentAtvCrStatusLabelComponent)
  .component('googleDirectionsMap', GoogleDirectionsMapComponent)
  .component('hotelAvailabilityCalendar', HotelAvailabilityCalendarComponent)
  .component('itemDetailStandardActions', ItemDetailStandardActionsComponent)
  .component('listActions', ListActionsComponent)
  .component('notifyDocumentToClient', NotifyDocumentToClientComponent)
  .component('productTaxExoneration', ProductTaxExonerationComponent)
  .component('relatedTransactionDetail', RelatedTransactionDetailComponent)
  .component('searchCabys', SearchCabysComponent)
  .component('searchClientInput', SearchClientInputComponent)
  .component('searchDiscountCouponInput', SearchDiscountCouponInputComponent)
  .component('searchFormActions', SearchFormActionsComponent)
  .component('searchFormExports', SearchFormExportsComponent)
  .component('searchProductInput', SearchProductInputComponent)
  .component('searchProviderInput', SearchProviderInputComponent)
  .component('searchTextInput', SearchTextInputComponent)
  .component('sortBy', SortByComponent)
  .component('tableFooter', TableFooterComponent)
  .component('photoList', PhotoListComponent)

  // Downgrade Angular components, directives and services to use it in Angularjs router/others

  // Components
  //.directive('appLanguageSelector', downgradeComponent({ component: LanguageSelectorComponent }))
  .directive(
    'appProductCategoryAccounting',
    downgradeComponent({ component: ProductCategoryAccountingComponent })
  )
  .directive('appProductAccounting', downgradeComponent({ component: ProductAccountingComponent }))

  .directive('appFooterCompany', downgradeComponent({ component: FooterCompanyComponent }))
  .directive('appFooterWebsite', downgradeComponent({ component: FooterWebsiteComponent }))

  .factory('ng2Router', downgradeInjectable(Router))

  // Services
  .factory('abilityService', downgradeInjectable(AbilityService))
  .factory('alertService', downgradeInjectable(AlertService))
  .factory('breadCrumbsService', downgradeInjectable(BreadCrumbsService))
  .factory('categoryService', downgradeInjectable(CategoryService))
  .factory('categoryModalService', downgradeInjectable(CategoryModalService))
  .factory('companyService', downgradeInjectable(CompanyService))
  .factory('companyModalService', downgradeInjectable(CompanyModalService))
  .factory('countryService', downgradeInjectable(CountryService))
  .factory('countryModalService', downgradeInjectable(CountryModalService))
  .factory('currencyService', downgradeInjectable(CurrencyService))
  .factory('currencyModalService', downgradeInjectable(CurrencyModalService))
  .factory('facilityService', downgradeInjectable(FacilityService))
  .factory('facilityModalService', downgradeInjectable(FacilityModalService))
  .factory('locationService', downgradeInjectable(LocationService))
  .factory('locationModalService', downgradeInjectable(LocationModalService))
  .factory('occupancyTypeService', downgradeInjectable(OccupancyTypeService))
  .factory('occupancyTypeModalService', downgradeInjectable(OccupancyTypeModalService))
  .factory('paxRangeService', downgradeInjectable(PaxRangeService))
  .factory('pickupPlaceService', downgradeInjectable(PickupPlaceService))
  .factory('pickupPlaceModalService', downgradeInjectable(PickupPlaceModalService))
  .factory('roomTypeService', downgradeInjectable(RoomTypeService))
  .factory('roomTypeModalService', downgradeInjectable(RoomTypeModalService))
  .factory('seasonService', downgradeInjectable(SeasonService))
  .factory('seasonModalService', downgradeInjectable(SeasonModalService))
  .factory('sessionService', downgradeInjectable(SessionService))
  .factory('translateService', downgradeInjectable(TranslateService))
  .factory('utilService', downgradeInjectable(UtilService))
  .factory('userService', downgradeInjectable(UserService))
  .factory('userModalService', downgradeInjectable(UserModalService))
  .factory('whatToBringService', downgradeInjectable(WhatToBringService))
  .factory('whatToBringModalService', downgradeInjectable(WhatToBringModalService))
  .factory('zoneService', downgradeInjectable(ZoneService))
  .factory('zoneModalService', downgradeInjectable(ZoneModalService))
  .factory('chartAccountService', downgradeInjectable(ChartAccountService));
